import { useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import { shopModeApi } from "../../api/shopMode";
import type { BelowFloorFlag } from "../../api/types";
import { useT } from "../../i18n/LanguageProvider";
import { money } from "../../i18n/intl";

/**
 * G-45 part 3, owner amendment 2026-08-24: the florist who charged a
 * below-floor price explains it right at the kiosk, immediately after the
 * sale. Its OWN element — the 2.5 s sale confirmation (OD-18) is untouched
 * and runs independently. "Later" is allowed (a customer is at the
 * counter): the sale simply stays on the owner's unjustified list and the
 * manager CRM door remains the backstop. A bouquet justification needs a
 * photo of the build — the server enforces it; the prompt offers the
 * camera inline when that is the refusal.
 */
export function BelowFloorPrompt({
  flags,
  onDone,
}: {
  flags: BelowFloorFlag[];
  onDone: () => void;
}) {
  const { t } = useT();
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [needsPhoto, setNeedsPhoto] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Record<string, boolean>>({});

  const remaining = flags.filter((f) => !done[f.saleLineId]);
  if (remaining.length === 0) return null;

  const submit = async (flag: BelowFloorFlag) => {
    const reason = (reasons[flag.saleLineId] ?? "").trim();
    if (reason.length < 3) {
      setError(t("belowFloor.reasonTooShort"));
      return;
    }
    setBusy(flag.saleLineId);
    setError(null);
    try {
      await shopModeApi.floorJustifyBelowFloor(flag.saleLineId, reason);
      setDone((cur) => ({ ...cur, [flag.saleLineId]: true }));
      if (flags.every((f) => done[f.saleLineId] || f.saleLineId === flag.saleLineId)) {
        onDone();
      }
    } catch (err: unknown) {
      const message =
        errorText(err, "belowFloor.couldNotSave");
      if (/photo/i.test(message) && flag.bouquetBuildId) {
        setNeedsPhoto((cur) => ({ ...cur, [flag.saleLineId]: true }));
        setError(t("belowFloor.photoFirst"));
      } else {
        setError(message);
      }
    } finally {
      setBusy(null);
    }
  };

  const attachPhoto = async (flag: BelowFloorFlag, file: File) => {
    if (!flag.bouquetBuildId) return;
    setBusy(flag.saleLineId);
    setError(null);
    try {
      await shopModeApi.floorAddBouquetPhoto(flag.bouquetBuildId, file);
      setNeedsPhoto((cur) => ({ ...cur, [flag.saleLineId]: false }));
    } catch (err: unknown) {
      setError(
        errorText(err, "belowFloor.couldNotUpload"),
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="card below-floor-prompt">
      <strong>{t("belowFloor.title")}</strong>
      <p className="muted">{t("belowFloor.copy")}</p>
      {error ? <div className="alert error">{error}</div> : null}
      {remaining.map((flag) => (
        <div key={flag.saleLineId} className="below-floor-prompt-line">
          <span>
            <strong>{flag.description}</strong> —{" "}
            {t("belowFloor.line", {
              price: money(flag.priceEur),
              floor: money(flag.floorEur),
              short: money(flag.shortfallEur),
            })}
          </span>
          {needsPhoto[flag.saleLineId] ? (
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void attachPhoto(flag, file);
              }}
            />
          ) : null}
          <textarea
            rows={2}
            maxLength={500}
            placeholder={t("belowFloor.placeholder")}
            value={reasons[flag.saleLineId] ?? ""}
            onChange={(e) =>
              setReasons((cur) => ({ ...cur, [flag.saleLineId]: e.target.value }))
            }
          />
          <button
            type="button"
            className="button"
            disabled={busy === flag.saleLineId}
            onClick={() => void submit(flag)}
          >
            {busy === flag.saleLineId ? t("belowFloor.saving") : t("belowFloor.saveReason")}
          </button>
        </div>
      ))}
      <button type="button" className="button plain" onClick={onDone}>
        {t("belowFloor.later")}
      </button>
    </div>
  );
}
